import { format } from 'date-fns';
import {
  Bug,
  Edit,
  Play,
  Shield
} from 'lucide-react';
import React from 'react';

interface ControlStatus {
  id: string;
  control_name: string;
  control_type: string;
  design_effectiveness: string;
  operating_effectiveness: string;
  design_effectiveness_score: number;
  operating_effectiveness_score: number;
  last_tested_date: string;
  next_testing_due: string;
  testing_frequency: string;
  current_status: 'Effective' | 'Partially Effective' | 'Ineffective' | 'Not Tested';
  owner: string;
  process_step_name: string;
  risk_count: number;
  gaps: ControlGap[];
}

interface ControlGap {
  id: string;
  gap_type: string;
  description: string;
  severity: 'High' | 'Medium' | 'Low';
  identified_date: string;
  status: 'Open' | 'In Progress' | 'Closed';
}

interface ControlsOverviewProps {
  controls: ControlStatus[];
  onEditControl?: (control: ControlStatus) => void;
  onTestControl?: (control: ControlStatus) => void;
}

export const ControlsOverview: React.FC<ControlsOverviewProps> = ({
  controls,
  onEditControl,
  onTestControl
}) => {
  return (
    <div className="bg-white rounded-lg shadow border">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-blue-600" />
          Control Status Overview
        </h3>

        {controls.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No controls available for this project
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Control Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Process Step</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effectiveness Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Tested</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Due</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Open Gaps</th>
                  {(onEditControl || onTestControl) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {controls.map((control) => (
                  <tr key={control.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {control.control_name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{control.process_step_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border">
                        {control.control_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        control.current_status.toLowerCase() === 'effective' ? 'bg-green-100 text-green-800' :
                        control.current_status.toLowerCase() === 'partially effective' ? 'bg-yellow-100 text-yellow-800' :
                        control.current_status.toLowerCase() === 'ineffective' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {control.current_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${
                              control.operating_effectiveness_score >= 70 ? 'bg-green-500' : 'bg-yellow-500'
                            }`}
                            style={{ width: `${control.operating_effectiveness_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {control.operating_effectiveness_score}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {control.last_tested_date ?
                        format(new Date(control.last_tested_date), 'MMM dd, yyyy') :
                        'Never'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm ${
                        control.next_testing_due && new Date(control.next_testing_due) < new Date() ? 'text-red-600' : 'text-gray-900'
                      }`}>
                        {control.next_testing_due ?
                          format(new Date(control.next_testing_due), 'MMM dd, yyyy') :
                          'Not Scheduled'
                        }
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Bug className="w-4 h-4 text-gray-400 mr-1" />
                        {control.gaps.length > 0 && (
                          <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                            {control.gaps.length}
                          </span>
                        )}
                      </div>
                    </td>
                    {(onEditControl || onTestControl) && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {onEditControl && (
                            <button
                              onClick={() => onEditControl(control)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit Control"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {onTestControl && (
                            <button
                              onClick={() => onTestControl(control)}
                              className="text-green-600 hover:text-green-900"
                              title="Test Control"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
